import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, MailCheck, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/site/Logo";
import { ChangePasswordDialog } from "../components/site/ChangePasswordDialog";
import { PhotoManager } from "../components/site/PhotoManager";
import { CertificateManager } from "../components/site/CertificateManager";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const RequestRow = ({ r, onToggle }) => (
  <tr data-testid="admin-request-row" className="border-b border-black/10 align-top">
    <td className="py-4 pr-4 font-mono text-xs text-[var(--muted-ink)]">{new Date(r.created_at).toLocaleString("en-IN")}</td>
    <td className="py-4 pr-4 text-sm font-semibold">{r.work_title}</td>
    <td className="py-4 pr-4 text-sm">
      <p className="font-semibold">{r.name}</p>
      <p className="text-[var(--muted-ink)]">{r.organisation}</p>
      {r.purpose && <p className="mt-1 text-xs italic text-[var(--muted-ink)]">{r.purpose}</p>}
    </td>
    <td className="space-y-1 py-4 pr-4 text-sm">
      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-[var(--rust)]"><Mail className="h-3 w-3" /> {r.email}</a>
      <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-[var(--rust)]"><Phone className="h-3 w-3" /> {r.phone}</a>
    </td>
    <td className="py-4">
      <button type="button" onClick={() => onToggle(r)} data-testid={`admin-request-status-${r.id}`}
        className={`px-3 py-1.5 font-mono text-xs uppercase ${r.status === "sent" ? "bg-[var(--forest)] text-white" : "bg-[var(--rust)] text-white"}`}>
        {r.status === "sent" ? "Sent" : "New · mark sent"}
      </button>
      <p data-testid={`admin-request-file-${r.id}`} className={`mt-1.5 text-xs ${r.certificate_on_file ? "text-[var(--forest)]" : "text-[var(--muted-ink)]"}`}>
        {r.certificate_on_file ? (r.user_id ? "File on record, reviewer can download" : "File on record (guest request, email only)") : "No file uploaded for this work"}
      </p>
      {r.requester_notified && <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-[var(--forest)]"><MailCheck className="h-3.5 w-3.5" /> Requester emailed</p>}
    </td>
  </tr>
);

export default function AdminRequests() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("requests");
  const { logout: authLogout } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/certificate-requests");
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) navigate("/admin/login");
      else toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    try {
      const { data } = await api.patch(`/certificate-requests/${r.id}`, { status: r.status === "sent" ? "new" : "sent" });
      setRows((rs) => rs.map((x) => (x.id === data.id ? data : x)));
      if (data.status === "sent") toast.success(data.requester_notified ? `Marked as sent. ${data.name} was notified by email.` : "Marked as sent. Email notification could not be delivered.");
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = async () => { await authLogout(); navigate("/admin/login"); };
  const pending = rows?.filter((r) => r.status === "new").length ?? 0;

  return (
    <main className="min-h-screen bg-[var(--paper)]">
      <title>Admin | Vata Infrastructures</title>
      <meta name="robots" content="noindex" />
      <header className="flex items-center justify-between border-b border-black/10 px-5 py-4 md:px-10">
        <Logo />
        <div className="flex items-center gap-6">
        <ChangePasswordDialog />
        <button type="button" onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm font-semibold uppercase"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <div className="mb-10 flex gap-2">
          {[["requests", `Certificate requests (${pending})`], ["files", "Certificate files"], ["photos", "Project photos"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`border px-4 py-2 text-sm font-semibold ${tab === id ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-black/20"}`}>{label}</button>
          ))}
        </div>
        {tab === "files" ? (
          <>
            <h1 className="mb-8 font-display text-5xl font-extrabold uppercase">Certificate files</h1>
            <CertificateManager />
          </>
        ) : tab === "photos" ? (
          <>
            <h1 className="mb-8 font-display text-5xl font-extrabold uppercase">Project photos</h1>
            <PhotoManager />
          </>
        ) : (
        <>
        <h1 className="font-display text-5xl font-extrabold uppercase">Certificate requests</h1>
        <p data-testid="admin-pending-count" className="mt-2 text-[var(--muted-ink)]">{pending} waiting to be sent</p>
        {rows === null ? <Loader2 className="mt-10 h-6 w-6 animate-spin" /> : rows.length === 0 ? (
          <p data-testid="admin-empty" className="mt-10 text-[var(--muted-ink)]">No requests yet.</p>
        ) : (
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[800px] text-left">
              <thead><tr className="border-b-2 border-[var(--ink)] font-mono text-xs uppercase text-[var(--muted-ink)]">
                <th className="py-3">Received</th><th>Work</th><th>Requested by</th><th>Contact</th><th>Status</th>
              </tr></thead>
              <tbody>{rows.map((r) => <RequestRow key={r.id} r={r} onToggle={toggle} />)}</tbody>
            </table>
          </div>
        )}
        </>
        )}
      </div>
    </main>
  );
}
